import { ConfirmDialog, InputDialog } from "@/components/ui";
import { formatList, useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { DatasetAddFieldDialog } from "./dataset-add-field-dialog";
import { FIELD_NAME_MAX_CHARS } from "./dataset-field-specs";
import type { DatasetFields } from "./use-dataset-fields";

/**
 * The open field dialog: Rename, Add field, or the confirmation of a change
 * that deletes data, which says what the preview reported (rows that lose a
 * value, charts reading the table).
 */
export function DatasetFieldDialogs({ fields }: { fields: DatasetFields }) {
  const t = useT(framesDatasetMessages);
  const { dialog } = fields;
  if (!dialog) return null;

  if (dialog.kind === "rename") {
    return (
      <InputDialog
        title={t("renameFieldTitle")}
        inputLabel={t("fieldName")}
        initialValue={dialog.field.name}
        maxLength={FIELD_NAME_MAX_CHARS}
        onCancel={fields.close}
        onSubmit={(name) => fields.rename(dialog.field.id, name)}
        data-testid="dataset-field-rename-dialog"
      />
    );
  }

  if (dialog.kind === "add") {
    return (
      <DatasetAddFieldDialog
        busy={fields.busy}
        onCancel={fields.close}
        onSubmit={(spec) => void fields.add(spec)}
      />
    );
  }

  const { field, option, impact } = dialog;
  const rows = t("rowsLose", { count: impact.affected_rows });
  const names = impact.consumers.map((consumer) => consumer.title);
  const charts =
    names.length > 0
      ? t(option ? "optionCharts" : "fieldCharts", {
          names: formatList(names),
        })
      : "";
  return (
    <ConfirmDialog
      title={
        option
          ? t("removeOptionTitle", { label: option.label, field: field.name })
          : t("deleteFieldTitle", { field: field.name })
      }
      description={charts ? `${rows} ${charts}` : rows}
      confirmLabel={t(option ? "removeOptionConfirm" : "deleteField")}
      busy={fields.busy}
      onConfirm={fields.confirm}
      onCancel={fields.close}
      data-testid="dataset-field-confirm"
    />
  );
}
